/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { LiveConnectConfig } from "@google/genai";

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
};

export function stopAudioBeforeDisconnect(
  streamer: Pick<AudioStreamer, "stop"> | null | undefined,
  liveClient: Pick<GenAILiveClient, "disconnect">
) {
  streamer?.stop();
  liveClient.disconnect();
}

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const client = useMemo(() => new GenAILiveClient(options), [options]);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [model, setModel] = useState<string>("models/gemini-2.0-flash-exp");
  const [config, setConfig] = useState<LiveConnectConfig>({});
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);
  // The vumeter worklet fires ~40Hz; routing every tick through React state
  // re-renders every context consumer (on a wall tablet that starves the
  // slideshow/clock timers and looks like a freeze). Gate to ~20Hz — smooth
  // enough for a VU meter, half the render load.
  const lastVolTs = useRef(0);
  // Auto-reconnect on UNEXPECTED session drops (Gemini Live GoAway / network blip):
  // a child mid-conversation must never be stranded on a dead "offline" glyph. We
  // reconnect with backoff, but NEVER when the close was intentional (lockout,
  // disarm, unmount) — tracked by intentionalRef.
  const intentionalRef = useRef(false);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<() => Promise<void>>(async () => {});

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        // The vu-meter worklet posts nothing on empty input, so volume would
        // freeze at its last mid-speech value when playback ends and hold the
        // orb in is-speaking forever. Zero it when the queue drains (also
        // fires on stop/interrupt).
        audioStreamerRef.current.onComplete = () => setVolume(0);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            const now = performance.now();
            if (now - lastVolTs.current < 45 && ev.data.volume > 0.001) return;
            lastVolTs.current = now;
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      reconnectAttempts.current = 0; // a clean open resets the backoff
      setConnected(true);
    };

    const onClose = () => {
      audioStreamerRef.current?.stop();
      setConnected(false);
      if (intentionalRef.current) return; // lockout / disarm / unmount → stay down
      // Unexpected drop mid-conversation → reconnect with exponential backoff.
      if (reconnectAttempts.current >= 6) return; // give up after ~6 tries (~30s)
      const delay = Math.min(15000, 1000 * 2 ** reconnectAttempts.current);
      reconnectAttempts.current += 1;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(() => {
        connectRef.current().catch(() => {});
      }, delay);
    };

    const onError = (error: ErrorEvent) => {
      console.error("error", error);
    };

    const stopAudioStreamer = () => audioStreamerRef.current?.stop();

    const onAudio = (data: ArrayBuffer) =>
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio);

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      intentionalRef.current = true; // unmount: never auto-reconnect a dead component
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio);
      stopAudioBeforeDisconnect(audioStreamerRef.current, client);
    };
  }, [client]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }
    intentionalRef.current = false; // a fresh connect is wanted → allow auto-reconnect
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    stopAudioBeforeDisconnect(audioStreamerRef.current, client);
    await client.connect(model, config);
  }, [client, config, model]);
  connectRef.current = connect; // keep the auto-reconnect callback pointing at the latest

  const disconnect = useCallback(async () => {
    intentionalRef.current = true; // user/logic-initiated → do NOT auto-reconnect
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    stopAudioBeforeDisconnect(audioStreamerRef.current, client);
    setConnected(false);
  }, [setConnected, client]);

  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    volume,
  };
}
